<script>
  import Layout from '/components/Layout.svelte';
  import DiagnosticTable from '/components/DiagnosticTable.svelte';
  import { inertia } from '@inertiajs/svelte';

  export let invalid_by_type = {};
  export let total_refs = 0;
  export let total_events = 0;

  const TYPE_LABELS = {
    skill:              'Skills',
    attribute:          'Attributes',
    skill_or_attribute: 'Skills / Attributes',
    location:           'Locations',
    effect:             'Effects',
  };

  $: types = Object.keys(invalid_by_type).sort();
  $: isEmpty = types.length === 0;

  function tableRows(rows) {
    return rows.map(r => ({ path: r.source_path, cells: r }));
  }
</script>

<svelte:head>
  <title>Invalid Asset References — Diagnostics</title>
</svelte:head>

<Layout title="Invalid Asset References">
  <div class="toolbar">
    <a use:inertia href="/" class="back-link">← Dashboard</a>
  </div>

  {#if isEmpty}
    <div class="all-clear">
      <span class="all-clear-icon">✓</span>
      All asset references are valid.
    </div>
  {:else}
    <p class="summary">
      {total_events} events contain {total_refs} references to skills, effects, locations, or attributes that don't exist.
    </p>

    {#each types as type}
      {@const rows = invalid_by_type[type]}
      {@const distinctIds = [...new Set(rows.flatMap(r => r.missing_ids))].sort().join('\n')}
      <DiagnosticTable
        title={TYPE_LABELS[type] ?? type}
        count="{rows.length} events"
        headers={['Source event', `Missing ${type} IDs`]}
        rows={tableRows(rows)}
        filename="invalid_{type}_references"
      >
        {#each rows as row}
          <tr>
            <td class="source-cell">
              <a use:inertia href="/editor/events/{row.source_path}" class="event-link">{row.source_event_id}</a>
            </td>
            <td class="pills-cell">
              {#each row.missing_ids as id}
                <span class="broken-id">{id}</span>
              {/each}
            </td>
          </tr>
        {/each}
        <tr class="distinct-row">
          <td colspan="2" class="distinct-cell">
            <label class="distinct-label">Distinct missing IDs</label>
            <textarea class="distinct-textarea" readonly rows={distinctIds.split('\n').length}>{distinctIds}</textarea>
          </td>
        </tr>
      </DiagnosticTable>
    {/each}
  {/if}
</Layout>

<style>
  .toolbar {
    display: flex;
    margin-bottom: 1.5rem;
  }

  .back-link {
    color: #64748b;
    text-decoration: none;
    font-size: 0.9rem;
  }

  .back-link:hover { color: #1e293b; }

  .summary {
    font-size: 0.9rem;
    color: #64748b;
    margin: 0 0 1.5rem 0;
  }

  .all-clear {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 1.25rem 1.5rem;
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 0.75rem;
    color: #166534;
    font-weight: 600;
  }

  .all-clear-icon { font-size: 1.1rem; }

  .distinct-row { background: #f8fafc; }

  .distinct-cell {
    padding: 0.75rem 1rem;
    border-top: 1px solid #e2e8f0;
  }

  .distinct-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.375rem;
  }

  .distinct-textarea {
    width: 100%;
    font-family: monospace;
    font-size: 0.8rem;
    color: #1e293b;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    padding: 0.5rem 0.625rem;
    resize: vertical;
    box-sizing: border-box;
  }
</style>
